import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Word } from '../models/Word';
import { WordService } from '../services/WordService';
import { HeartIcon as HeartOutline, CheckIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import WordDefinition from './WordDefinition';

const WordList: React.FC = () => {
    const [words, setWords] = useState<Word[]>([]);
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const observer = useRef<IntersectionObserver | null>(null);
    const [selectedWord, setSelectedWord] = useState<{
        word: string;
        position: { x: number; y: number };
    } | null>(null);

    const lastWordElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading || !hasMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                setPage((prevPage: number) => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        const fetchMoreWords = async () => {
            setLoading(true);
            try {
                const newWords = await WordService.fetchWords(1, page, 20);
                if (newWords.length === 0) {
                    setHasMore(false);
                } else {
                    setWords((prev: Word[]) => [...prev, ...newWords]);
                }
            } catch (error) {
                console.error('Error fetching words:', error);
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        };
        fetchMoreWords();
    }, [page]);

    const handleFilledHeartClick = async (wordIndex: number) => {
        const newStar = Math.max(0, words[wordIndex].star - 1);
        
        // First update locally
        setWords((prevWords: Word[]) => {
            const newWords = [...prevWords];
            newWords[wordIndex] = {
                ...newWords[wordIndex],
                star: newStar
            };
            return newWords;
        });

        // Then sync with server
        try {
            const success = await WordService.updateStar(1, words[wordIndex].word, newStar);
            if (!success) {
                console.error('Failed to update star value on server');
                // Optionally revert the local change if server update fails
                setWords((prevWords: Word[]) => {
                    const newWords = [...prevWords];
                    newWords[wordIndex] = {
                        ...newWords[wordIndex],
                        star: words[wordIndex].star // Revert to original value
                    };
                    return newWords;
                });
            }
        } catch (error) {
            console.error('Error updating star:', error);
        }
    };

    const handleHollowHeartClick = async (wordIndex: number) => {
        const newStar = words[wordIndex].star + 1;
        
        // First update locally
        setWords((prevWords: Word[]) => {
            const newWords = [...prevWords];
            newWords[wordIndex] = {
                ...newWords[wordIndex],
                star: newStar
            };
            return newWords;
        });

        // Then sync with server
        try {
            const success = await WordService.updateStar(1, words[wordIndex].word, newStar);
            if (!success) {
                console.error('Failed to update star value on server');
                // Optionally revert the local change if server update fails
                setWords((prevWords: Word[]) => {
                    const newWords = [...prevWords];
                    newWords[wordIndex] = {
                        ...newWords[wordIndex],
                        star: words[wordIndex].star - 1 // Revert to original value
                    };
                    return newWords;
                });
            }
        } catch (error) {
            console.error('Error updating star:', error);
        }
    };

    const handleDelete = async (wordIndex: number) => {
        try {
            const success = await WordService.updateStar(
                1, 
                words[wordIndex].word, 
                words[wordIndex].star,
                true // set del_flag to true
            );
            
            if (success) {
                // Remove the word from local state
                setWords((prevWords: Word[]) => 
                    prevWords.filter((_, index) => index !== wordIndex)
                );
            } else {
                console.error('Failed to delete word');
            }
        } catch (error) {
            console.error('Error deleting word:', error);
        }
    };

    const handleWordClick = (word: string, event: React.MouseEvent) => {
        setSelectedWord({
            word,
            position: {
                x: event.clientX,
                y: event.clientY
            }
        });
    };

    const renderHearts = (word: Word, index: number) => (
        <div
            key={word.word + index}
            ref={index === words.length - 1 ? lastWordElementRef : null}
            className="flex items-center p-4 border-b"
        >
            <span 
                className="text-lg flex-1 cursor-pointer hover:text-blue-600"
                onClick={(e) => handleWordClick(word.word, e)}
            >
                {word.word}
            </span>
            <div className="flex items-center space-x-1">
                {[...Array(word.star)].map((_, starIndex: number) => (
                    <HeartSolid
                        key={starIndex}
                        className="h-6 w-6 text-red-500 cursor-pointer"
                        onClick={() => handleFilledHeartClick(index)}
                    />
                ))}
                <div className="relative cursor-pointer w-6 h-6" onClick={() => handleDelete(index)}>
                    <CheckIcon 
                        className="h-5 w-5 text-gray-500 absolute top-0 left-0" 
                    />
                    <CheckIcon 
                        className="h-3 w-3 text-gray-500 absolute top-1.5 left-2" 
                        // style={{ 
                        //     bottom: '0.2px',
                        //     right: '1px'
                        // }}
                    />
                </div>
                <HeartOutline
                    className="h-6 w-6 text-red-500 cursor-pointer"
                    onClick={() => handleHollowHeartClick(index)}
                />
            </div>
        </div>
    );

    return (
        <div className="max-w-2xl mx-auto p-4">
            {words.map((word: Word, index: number) => renderHearts(word, index))}
            {loading && <div className="text-center p-4">Loading...</div>}
            {!hasMore && words.length === 0 && (
                <div className="text-center p-4 text-gray-500">No words found</div>
            )}
            {!hasMore && words.length > 0 && (
                <div className="text-center p-4 text-gray-500">No more words to load</div>
            )}
            
            {selectedWord && (
                <WordDefinition
                    word={selectedWord.word}
                    position={selectedWord.position}
                    onClose={() => setSelectedWord(null)}
                />
            )}
        </div>
    );
};

export default WordList; 